import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Trash2, GripVertical, Save, Eye, Send, Copy, Check, BarChart3,
  Hash, Smile, ListChecks, ListOrdered, AlignLeft, ChevronUp, ChevronDown, MessageSquare, Star, Sliders, Languages,
  GitBranch, FileText as FilePlus, Variable, ThumbsUp, ThumbsDown
} from "lucide-react";
import { OPERATORS, BRANCH_OPERATORS } from "@/lib/logic";
import RichEditor from "@/components/RichEditor";

const QTYPES = [
  { type: "nps", label: "NPS (0–10)", icon: <Hash size={16}/>, defaults: { min: 0, max: 10, required: true } },
  { type: "csat", label: "CSAT (1–5)", icon: <Star size={16}/>, defaults: { min: 1, max: 5, required: true } },
  { type: "ces", label: "CES (1–7)", icon: <Sliders size={16}/>, defaults: { min: 1, max: 7, required: true } },
  { type: "rating", label: "Star rating", icon: <Star size={16}/>, defaults: { min: 1, max: 5, required: true } },
  { type: "emoji", label: "Emoji scale", icon: <Smile size={16}/>, defaults: { options: ["😡","😞","😐","🙂","😍"], required: true } },
  { type: "yes_no", label: "Yes / No (thumbs)", icon: <ThumbsUp size={16}/>, defaults: { required: true } },
  { type: "mcq", label: "Single choice (MCQ)", icon: <ListChecks size={16}/>, defaults: { options: ["Option 1", "Option 2", "Option 3"], required: true } },
  { type: "multi_select", label: "Multi-select", icon: <ListChecks size={16}/>, defaults: { options: ["Option 1","Option 2","Option 3"], required: false } },
  { type: "dropdown", label: "Dropdown", icon: <ListChecks size={16}/>, defaults: { options: ["Option 1","Option 2"], required: true } },
  { type: "ranking", label: "Ranking", icon: <ListOrdered size={16}/>, defaults: { options: ["Item A","Item B","Item C"], required: true } },
  { type: "slider", label: "Slider", icon: <Sliders size={16}/>, defaults: { min: 0, max: 100, required: true } },
  { type: "numeric", label: "Numeric input", icon: <Hash size={16}/>, defaults: { placeholder: "Enter a number", required: true, validation: { min_value: 0, max_value: 9999 } } },
  { type: "text", label: "Short text", icon: <AlignLeft size={16}/>, defaults: { placeholder: "Your answer", required: false } },
  { type: "long_text", label: "Long text", icon: <MessageSquare size={16}/>, defaults: { placeholder: "Your detailed feedback", required: false } },
  { type: "email", label: "Email", icon: <AlignLeft size={16}/>, defaults: { placeholder: "you@example.com", required: true } },
  { type: "phone", label: "Phone", icon: <AlignLeft size={16}/>, defaults: { placeholder: "+1 555 0100", required: true } },
  { type: "date", label: "Date", icon: <AlignLeft size={16}/>, defaults: { required: true } },
  { type: "time", label: "Time", icon: <AlignLeft size={16}/>, defaults: { required: true } },
  { type: "image_choice", label: "Image choice", icon: <ListChecks size={16}/>, defaults: { options: ["https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300", "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300"], required: true } },
  { type: "file_upload", label: "File upload", icon: <AlignLeft size={16}/>, defaults: { placeholder: "Attach a file", required: false, allowed_extensions: ["pdf","jpg","jpeg","png"] } },
];

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export default function SurveyBuilderPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${id}`).then((r) => setSurvey(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [id]);

  const setField = (k, v) => setSurvey((s) => ({ ...s, [k]: v }));

  const [pageHint, setPageHint] = useState(null);

  const addQuestion = (type) => {
    const def = QTYPES.find((q) => q.type === type);
    const lastPage = (survey.questions || []).reduce((m, q) => Math.max(m, q.page || 1), 1);
    const targetPage = pageHint && pageHint > lastPage ? pageHint : lastPage;
    const q = {
      id: newId(),
      type,
      title: def.label + " question",
      required: def?.defaults?.required ?? true,
      page: targetPage,
      ...def.defaults,
    };
    setSurvey((s) => ({ ...s, questions: [...(s.questions || []), q] }));
    setPageHint(null);
  };

  const addPageBreak = () => {
    const lastPage = (survey.questions || []).reduce((m, q) => Math.max(m, q.page || 1), 1);
    const newPage = lastPage + 1;
    const sections = [...(survey.sections || []), { page: newPage, title: `Section ${newPage}`, description: "" }];
    setSurvey((s) => ({ ...s, sections }));
    setPageHint(newPage);
  };

  const updateSection = (page, patch) => {
    setSurvey((s) => ({
      ...s,
      sections: (s.sections || []).map((sec) => (sec.page === page ? { ...sec, ...patch } : sec)),
    }));
  };

  const removePageBreak = (page) => {
    setSurvey((s) => ({
      ...s,
      // Remove the section row
      sections: (s.sections || []).filter((sec) => sec.page !== page),
      // Move questions on this page back to (page-1)
      questions: (s.questions || []).map((q) => (q.page === page ? { ...q, page: Math.max(1, page - 1) } : q)),
    }));
  };

  const updQ = (qid, patch) => {
    setSurvey((s) => ({ ...s, questions: s.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)) }));
  };
  const delQ = (qid) => setSurvey((s) => ({ ...s, questions: s.questions.filter((q) => q.id !== qid) }));
  const moveQ = (idx, dir) => {
    setSurvey((s) => {
      const arr = [...s.questions];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return s;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...s, questions: arr };
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put(`/surveys/${id}`, {
        title: survey.title,
        description: survey.description,
        questions: survey.questions,
        sections: survey.sections || [],
        welcome_message: survey.welcome_message,
        thank_you_message: survey.thank_you_message,
        default_language: survey.default_language,
        capture_params: survey.capture_params,
        monthly_response_limit: survey.monthly_response_limit ?? 0,
        display_mode: survey.display_mode || "stepper",
        header_logo_url: survey.header_logo_url || null,
        show_progress_bar: survey.show_progress_bar !== false,
        theme: survey.theme,
        // Sprint 1 additions
        status: survey.status,
        starts_at: survey.starts_at ?? null,
        expires_at: survey.expires_at ?? null,
        cooldown_days: survey.cooldown_days ?? 0,
        once_per_ref: survey.once_per_ref === true,
        journeys: survey.journeys ?? null,
      });
      setSurvey(data);
      setSavedAt(new Date());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    await save();
    setSaving(true);
    try {
      const { data } = await api.post(`/surveys/${id}/publish`);
      setSurvey(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const publicUrl = useMemo(() => {
    if (!survey) return "";
    return `${window.location.origin}/r/${survey.public_slug}`;
  }, [survey]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  if (!survey) return <div className="text-slate-500">{err || "Loading…"}</div>;

  return (
    <div className="space-y-5 fade-in">
      {/* top bar */}
      <Card className="p-5 border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1">
            <Input data-testid="builder-title" value={survey.title} onChange={(e) => setField("title", e.target.value)} className="text-xl font-extrabold text-[#1F3A6B] border-0 px-0 focus-visible:ring-0 h-9"/>
            <Textarea data-testid="builder-desc" value={survey.description || ""} onChange={(e) => setField("description", e.target.value)} placeholder="Add a description (optional)" rows={1} className="mt-1 border-0 px-0 focus-visible:ring-0 resize-none text-slate-600"/>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge className={survey.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{survey.status}</Badge>
            <TranslateButton surveyId={id} survey={survey} setSurvey={setSurvey}/>
            <Link to={`/app/surveys/${id}/analytics`}><Button data-testid="builder-analytics-btn" variant="outline"><BarChart3 size={16} className="mr-1.5"/>Analytics</Button></Link>
            <Button data-testid="builder-save-btn" onClick={save} disabled={saving} variant="outline"><Save size={16} className="mr-1.5"/>{saving ? "Saving…" : "Save"}</Button>
            <Button data-testid="builder-publish-btn" onClick={publish} disabled={saving} className="bg-[#F58220] hover:bg-[#E36F0E] text-white"><Send size={16} className="mr-1.5"/>{survey.status === "published" ? "Update & Publish" : "Publish"}</Button>
          </div>
        </div>
        {savedAt && <p className="mt-2 text-xs text-emerald-600">Saved at {savedAt.toLocaleTimeString()}</p>}
        {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
        {survey.status === "published" && (
          <div className="mt-3 flex items-center gap-2 text-sm bg-[#FFF0DF] border border-[#FFE1BE] rounded-md px-3 py-2">
            <Eye size={16} className="text-[#F58220]"/>
            <span className="text-slate-700 truncate">{publicUrl}</span>
            <Button data-testid="builder-copy-link" size="sm" variant="ghost" onClick={copyLink} className="ml-auto h-7">{copied ? <><Check size={14} className="mr-1 text-emerald-600"/>Copied</> : <><Copy size={14} className="mr-1"/>Copy</>}</Button>
            <a href={publicUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-7">Open</Button></a>
          </div>
        )}

        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Default language</Label>
            <select
              data-testid="builder-default-language"
              value={(survey.default_language || "english").toLowerCase()}
              onChange={(e) => setField("default_language", e.target.value)}
              className="mt-1 h-10 w-full border border-slate-200 rounded-md px-3 bg-white text-sm capitalize"
            >
              {["english","spanish","hindi","french","german","portuguese","italian","arabic","japanese","chinese (simplified)","korean","russian","turkish","dutch","polish","vietnamese","indonesian","thai","hebrew"].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">Respondents see this language by default. Use <strong>Translate</strong> to add more.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Capture URL parameters</Label>
            <Input
              data-testid="builder-capture-params"
              value={(survey.capture_params || []).join(", ")}
              onChange={(e) => setField("capture_params", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="store_code, campaign, region"
              className="mt-1 h-10"
            />
            <p className="mt-1 text-[11px] text-slate-500">e.g. <code>?phone=...&email=...&store_code=A12</code> auto-fills respondent + saves to metadata.</p>
          </div>
        </div>

        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Max responses per respondent / month</Label>
            <Input
              type="number"
              min={0}
              data-testid="builder-monthly-limit"
              value={survey.monthly_response_limit ?? 0}
              onChange={(e) => setField("monthly_response_limit", Math.max(0, Number(e.target.value) || 0))}
              placeholder="0 = unlimited"
              className="mt-1 h-10"
            />
            <p className="mt-1 text-[11px] text-slate-500">Limits the same email or phone from submitting more than N times in a calendar month. Set <code>0</code> for unlimited.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Display mode</Label>
            <select
              data-testid="builder-display-mode"
              value={survey.display_mode || "stepper"}
              onChange={(e) => setField("display_mode", e.target.value)}
              className="mt-1 h-10 w-full border border-slate-200 rounded-md px-3 bg-white text-sm"
            >
              <option value="stepper">One question at a time (Back/Next)</option>
              <option value="single_page">All questions on a single page</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-500">Single-page mode is great for short surveys — no Back/Next buttons, only one Submit.</p>
          </div>
        </div>

        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Override brand logo (this survey only)</Label>
            <Input
              data-testid="builder-header-logo"
              value={survey.header_logo_url || ""}
              onChange={(e) => setField("header_logo_url", e.target.value)}
              placeholder="https://acme.com/logo.png — leave blank to use workspace logo"
              className="mt-1 h-10"
            />
            <p className="mt-1 text-[11px] text-slate-500">Shown in the public survey header & footer. Workspace logo (Settings → Branding) is used by default.</p>
          </div>
          <div className="flex items-center gap-2 mt-6 sm:mt-7">
            <Switch
              data-testid="builder-show-progress"
              checked={survey.show_progress_bar !== false}
              onCheckedChange={(v) => setField("show_progress_bar", v)}
            />
            <span className="text-sm text-slate-700">Show progress bar</span>
          </div>
        </div>

        <ThemeColorsEditor theme={survey.theme || {}} onChange={(patch) => setField("theme", { ...(survey.theme || {}), ...patch })}/>

        <SurveyScheduleEditor survey={survey} setField={setField}/>
        <SurveyJourneysEditor journeys={survey.journeys || {}} onChange={(v) => setField("journeys", v)}/>
      </Card>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Question palette */}
        <Card className="lg:col-span-3 p-4 border-slate-100 h-fit sticky top-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Add question</p>
          <div className="mt-3 grid grid-cols-1 gap-1.5">
            {QTYPES.map((q) => (
              <button
                key={q.type}
                data-testid={`add-q-${q.type}`}
                onClick={() => addQuestion(q.type)}
                className="flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-left text-slate-700 hover:bg-[#FFF0DF] hover:text-[#1F3A6B] transition-colors"
              >
                <span className="text-[#F58220]">{q.icon}</span>
                {q.label}
                <Plus size={14} className="ml-auto text-slate-400"/>
              </button>
            ))}
          </div>
        </Card>

        {/* Editor */}
        <div className="lg:col-span-6 space-y-3">
          <Card className="p-5 border-slate-100">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Welcome message</Label>
            <div className="mt-1.5">
              <RichEditor
                testId="builder-welcome"
                value={survey.welcome_message || ""}
                onChange={(v) => setField("welcome_message", v)}
                placeholder="Greet your respondents…"
                singleLine={false}
                minHeight={44}
              />
            </div>
          </Card>

          {(() => {
            const qs = survey.questions || [];
            const sectionsByPage = Object.fromEntries((survey.sections || []).map((s) => [s.page, s]));
            // Group questions by page (sorted by current array order, but pages render in numeric order)
            const pages = [];
            const byPage = {};
            qs.forEach((q, idx) => {
              const p = q.page || 1;
              if (!byPage[p]) { byPage[p] = []; pages.push(p); }
              byPage[p].push({ q, idx });
            });
            // Make sure declared sections also produce a header even if empty
            (survey.sections || []).forEach((s) => { if (!byPage[s.page]) { byPage[s.page] = []; pages.push(s.page); } });
            pages.sort((a, b) => a - b);

            return pages.map((p, pi) => {
              const sec = sectionsByPage[p];
              const items = byPage[p] || [];
              return (
                <React.Fragment key={`page-${p}`}>
                  {pi > 0 && (
                    <Card data-testid={`section-card-${p}`} className="p-4 border-cyan-200 bg-[#FFF0DF]/40 border-dashed">
                      <div className="flex items-center gap-2 mb-2">
                        <FilePlus size={14} className="text-[#F58220]"/>
                        <p className="text-xs uppercase tracking-wider text-[#1F3A6B] font-bold">Page {p} · Section</p>
                        <Button data-testid={`section-remove-${p}`} size="icon" variant="ghost" className="h-7 w-7 ml-auto text-rose-600" onClick={() => removePageBreak(p)}><Trash2 size={14}/></Button>
                      </div>
                      <Input
                        data-testid={`section-title-${p}`}
                        value={sec?.title || ""}
                        onChange={(e) => updateSection(p, { title: e.target.value })}
                        placeholder="Section title (shown on the response page)"
                        className="bg-white"
                      />
                      <Input
                        value={sec?.description || ""}
                        onChange={(e) => updateSection(p, { description: e.target.value })}
                        placeholder="Section description (optional)"
                        className="mt-1.5 bg-white text-sm"
                      />
                    </Card>
                  )}
                  {items.map(({ q, idx }) => (
                    <QuestionEditor
                      key={q.id}
                      index={idx + 1}
                      q={q}
                      allQuestions={qs.slice(0, idx)}
                      surveyQuestions={qs}
                      onChange={(patch) => updQ(q.id, patch)}
                      onDelete={() => delQ(q.id)}
                      onMoveUp={() => moveQ(idx, -1)}
                      onMoveDown={() => moveQ(idx, 1)}
                    />
                  ))}
                </React.Fragment>
              );
            });
          })()}

          {(survey.questions || []).length > 0 && (
            <Button data-testid="builder-add-page-break" type="button" onClick={addPageBreak} variant="outline" className="w-full border-dashed">
              <FilePlus size={14} className="mr-1.5 text-[#F58220]"/>Add page break
            </Button>
          )}

          {(!survey.questions || survey.questions.length === 0) && (
            <Card className="p-10 border-dashed border-2 border-slate-200 text-center">
              <p className="font-bold text-[#1F3A6B]">No questions yet</p>
              <p className="text-sm text-slate-600">Add a question from the left palette to get started.</p>
            </Card>
          )}

          <Card className="p-5 border-slate-100">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Thank-you message</Label>
            <div className="mt-1.5">
              <RichEditor
                testId="builder-thankyou"
                value={survey.thank_you_message || ""}
                onChange={(v) => setField("thank_you_message", v)}
                placeholder="Say thanks…"
                singleLine={false}
                minHeight={44}
              />
            </div>
          </Card>
        </div>

        {/* Live Preview */}
        <div className="lg:col-span-3">
          <DevicePreview slug={survey.public_slug} savedAt={savedAt} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Device-frame live preview ---------- */
function DevicePreview({ slug, savedAt }) {
  const [device, setDevice] = React.useState("desktop"); // desktop | tablet | mobile
  const [reloadKey, setReloadKey] = React.useState(0);
  const containerRef = React.useRef(null);
  const [containerW, setContainerW] = React.useState(0);
  // Auto-refresh the iframe whenever a save completes so preview reflects the latest theme/questions.
  React.useEffect(() => { setReloadKey((k) => k + 1); }, [savedAt]);

  // Measure the available container width so we can scale device frames to fit.
  React.useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const measure = () => setContainerW(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dims = {
    desktop: { w: 1440, h: 900, label: "Desktop", chip: "1440 × 900" },
    tablet:  { w: 768,  h: 1024, label: "Tablet",  chip: "768 × 1024" },
    mobile:  { w: 375,  h: 812, label: "Mobile",  chip: "375 × 812" },
  }[device];

  if (!slug) {
    return (
      <Card className="p-5 border-slate-100 sticky top-6">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Live preview</p>
        <p className="mt-2 text-sm text-slate-500">Save the survey once to enable the live preview.</p>
      </Card>
    );
  }

  const src = `${window.location.origin}/r/${slug}?preview=1&_=${reloadKey}`;
  // Scale the device frame so it always fits inside the (narrow) preview column.
  const padded = Math.max(240, (containerW || 360) - 24);
  const scale = Math.min(1, padded / dims.w);
  const scaledH = Math.min(720, dims.h * scale);

  return (
    <Card className="p-0 overflow-hidden border-slate-100 sticky top-6">
      <div className="bg-[#F58220] text-white px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2">
        <span>Live preview</span>
        <div className="flex items-center gap-1 bg-white/10 rounded-md p-0.5">
          {["desktop", "tablet", "mobile"].map((d) => (
            <button
              key={d}
              data-testid={`preview-device-${d}`}
              onClick={() => setDevice(d)}
              className={`px-2 py-1 rounded text-[11px] font-semibold capitalize transition-colors ${device === d ? "bg-white text-[#1F3A6B]" : "text-white/80 hover:text-white"}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} className="bg-slate-100 p-3 flex items-start justify-center overflow-hidden" style={{ minHeight: 380 }}>
        {/* Scaled container: keeps device frame at its true CSS width but visually fits the column */}
        <div style={{ width: dims.w * scale, height: scaledH }} className="relative">
          <div
            className="bg-white rounded-lg shadow-lg overflow-hidden ring-1 ring-slate-200 origin-top-left transition-transform"
            style={{
              width: dims.w,
              height: dims.h,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <iframe
              key={reloadKey}
              title="Survey live preview"
              src={src}
              data-testid="preview-iframe"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>{dims.label} · {dims.chip}{scale < 1 && ` · ${Math.round(scale * 100)}% zoom`}</span>
        <div className="flex items-center gap-2">
          <button data-testid="preview-refresh" className="hover:text-[#1F3A6B] font-semibold" onClick={() => setReloadKey((k) => k + 1)}>Refresh</button>
          <a data-testid="preview-open-tab" href={src} target="_blank" rel="noreferrer" className="hover:text-[#1F3A6B] font-semibold">Open in tab ↗</a>
        </div>
      </div>
    </Card>
  );
}

function QuestionEditor({ index, q, allQuestions = [], surveyQuestions = [], onChange, onDelete, onMoveUp, onMoveDown }) {
  const [logicOpen, setLogicOpen] = useState(false);
  const titleEditorRef = React.useRef(null);
  const descEditorRef = React.useRef(null);
  const lastFocusedRef = React.useRef("title"); // "title" | "description"

  const insertPiping = (qid) => {
    const target = lastFocusedRef.current === "description" ? descEditorRef.current : titleEditorRef.current;
    const token = `{{q:${qid}}}`;
    if (target && target.chain) {
      target.chain().focus().insertContent(token).run();
    } else {
      // Fallback: append to title
      onChange({ title: (q.title || "") + token });
    }
  };

  return (
    <Card data-testid={`q-editor-${q.id}`} className="p-5 border-slate-100 group">
      <div className="flex items-start gap-3">
        <div className="pt-1 text-slate-400 drag-handle"><GripVertical size={18}/></div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-semibold text-[#1F3A6B]">Q{index}</span>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE]">{q.type}</Badge>
            {(q.display_when?.length || q.branch_to?.length) ? (
              <Badge className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-100"><GitBranch size={10} className="mr-1"/>logic</Badge>
            ) : null}
            <div className="ml-auto flex items-center gap-1">
              {allQuestions.length > 0 && (
                <PipingMenu
                  testId={`q-piping-${q.id}`}
                  questions={allQuestions}
                  onPick={(qid) => insertPiping(qid)}
                />
              )}
              <Button data-testid={`q-up-${q.id}`} size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveUp}><ChevronUp size={14}/></Button>
              <Button data-testid={`q-down-${q.id}`} size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveDown}><ChevronDown size={14}/></Button>
              <Button data-testid={`q-del-${q.id}`} size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700" onClick={onDelete}><Trash2 size={14}/></Button>
            </div>
          </div>
          <div className="mt-2">
            <RichEditor
              testId={`q-title-${q.id}`}
              value={q.title || ""}
              onChange={(v) => onChange({ title: v })}
              placeholder="Question"
              singleLine
              minHeight={40}
              editorRef={titleEditorRef}
              onFocus={() => { lastFocusedRef.current = "title"; }}
            />
          </div>
          <div className="mt-2">
            <RichEditor
              testId={`q-desc-${q.id}`}
              value={q.description || ""}
              onChange={(v) => onChange({ description: v })}
              placeholder="Help text (optional)"
              singleLine={false}
              minHeight={36}
              editorRef={descEditorRef}
              onFocus={() => { lastFocusedRef.current = "description"; }}
            />
          </div>

          <div className="mt-3">
            {(q.type === "mcq" || q.type === "multi_select" || q.type === "dropdown" || q.type === "ranking" || q.type === "image_choice" || q.type === "emoji") && (
              <OptionsEditor q={q} onChange={onChange} />
            )}
            {(q.type === "rating" || q.type === "csat" || q.type === "ces" || q.type === "slider") && (
              <RangeEditor q={q} onChange={onChange} />
            )}
            {(q.type === "text" || q.type === "long_text" || q.type === "email" || q.type === "phone" || q.type === "numeric") && (
              <Input data-testid={`q-placeholder-${q.id}`} value={q.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })} placeholder="Placeholder text" className="h-9"/>
            )}
            {q.type === "numeric" && (
              <NumericValidationEditor q={q} onChange={onChange}/>
            )}
            {q.type === "date" && (
              <DateValidationEditor q={q} onChange={onChange}/>
            )}
            {q.type === "file_upload" && (
              <FileExtensionsEditor q={q} onChange={onChange}/>
            )}
          </div>

          <ExtraSettings q={q} onChange={onChange}/>

          <div className="mt-4 flex items-center gap-2 text-sm">
            <Switch data-testid={`q-required-${q.id}`} checked={!!q.required} onCheckedChange={(v) => onChange({ required: v })}/>
            <span className="text-slate-600">Required</span>
            <Button
              data-testid={`q-logic-toggle-${q.id}`}
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setLogicOpen((o) => !o)}
              className="ml-auto text-[#1F3A6B]"
            >
              <GitBranch size={14} className="mr-1.5"/>{logicOpen ? "Hide logic" : "Logic"}
            </Button>
          </div>

          {logicOpen && (
            <div className="mt-4 border-t border-slate-100 pt-4 space-y-4">
              <DisplayWhenEditor q={q} allQuestions={allQuestions} onChange={onChange} />
              <BranchEditor q={q} allQuestions={allQuestions} onChange={onChange} />
            </div>
          )}

          {q.type === "nps" && (
            <NpsMessagesEditor q={q} onChange={onChange}/>
          )}
        </div>
      </div>
    </Card>
  );
}

function NpsMessagesEditor({ q, onChange }) {
  const [open, setOpen] = React.useState(false);
  const msgs = q.nps_messages || {};
  const update = (key, val) => onChange({ nps_messages: { ...msgs, [key]: val } });
  const presets = {
    detractor: "<p><strong>We're sorry to hear that!</strong></p><p>Please do let us know what went wrong and what we can do to serve you better next time.</p>",
    passive:   "<p><strong>We appreciate your feedback!</strong></p><p>We're always looking for ways to improve. Please tell us what we can do to make your experience even better.</p>",
    promoter:  "<p><strong>We appreciate your positive feedback!</strong></p><p>We're delighted to hear that you had a great experience. Your satisfaction is our top priority — we look forward to serving you again soon!</p>",
  };
  const applyAll = () => onChange({ nps_messages: { ...presets, ...msgs } });
  const hasAny = !!(msgs.detractor || msgs.passive || msgs.promoter);

  const blocks = [
    { key: "detractor", label: "Detractors (0–6)", color: "rose", border: "border-rose-200", swatch: "bg-rose-500" },
    { key: "passive",   label: "Passives (7–8)",   color: "amber", border: "border-amber-200", swatch: "bg-amber-400" },
    { key: "promoter",  label: "Promoters (9–10)", color: "emerald", border: "border-emerald-200", swatch: "bg-emerald-500" },
  ];

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid={`nps-messages-toggle-${q.id}`}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-[#1F3A6B] hover:text-[#082F48]"
        >
          <MessageSquare size={14}/>
          NPS feedback messages
          {hasAny && <Badge className="text-[10px] bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE]" variant="outline">configured</Badge>}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
        </button>
        {open && !hasAny && (
          <Button type="button" size="sm" variant="outline" onClick={applyAll} data-testid={`nps-messages-apply-presets-${q.id}`}>
            Apply preset messages
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-slate-500">Shown to the respondent immediately after they pick a score. Supports formatting + color via the rich editor.</p>
          {blocks.map((b) => (
            <div key={b.key} className={`border rounded-md p-3 ${b.border} bg-white`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${b.swatch}`}/>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{b.label}</span>
                {msgs[b.key] && (
                  <button
                    type="button"
                    onClick={() => update(b.key, "")}
                    data-testid={`nps-message-clear-${q.id}-${b.key}`}
                    className="ml-auto text-[11px] text-rose-600 hover:underline"
                  >Clear</button>
                )}
              </div>
              <RichEditor
                testId={`nps-message-${q.id}-${b.key}`}
                value={msgs[b.key] || ""}
                onChange={(v) => update(b.key, v)}
                placeholder={`E.g. ${presets[b.key].replace(/<\/?[^>]+>/g, "").slice(0, 80)}…`}
                minHeight={44}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PipingMenu({ questions, onPick, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button data-testid={testId} type="button" size="icon" variant="ghost" className="h-7 w-7 text-[#F58220]" onClick={() => setOpen((o) => !o)} title="Insert previous answer">
        <Variable size={14}/>
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-elev-2 z-20 max-h-72 overflow-auto">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Insert answer from</p>
          </div>
          <ul className="text-sm">
            {questions.map((q, i) => (
              <li key={q.id}>
                <button
                  onClick={() => { onPick(q.id); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-[#FFF0DF] truncate"
                >
                  <span className="text-[#F58220] font-bold mr-2">Q{i + 1}</span>
                  <span className="text-slate-700">{q.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DisplayWhenEditor({ q, allQuestions, onChange }) {
  const rules = q.display_when || [];
  const matchMode = q.display_when_match === "any" ? "any" : "all";
  const update = (i, patch) => onChange({ display_when: rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const remove = (i) => onChange({ display_when: rules.filter((_, idx) => idx !== i) });
  const add = (preset) => {
    const base = { question_id: allQuestions[0]?.id, operator: "equals", value: "" };
    onChange({ display_when: [...rules, preset ? { ...base, ...preset } : base] });
  };

  // Detect if any earlier question is NPS/CSAT/rating/slider/numeric to offer presets
  const numericQs = allQuestions.filter((aq) => ["nps", "csat", "ces", "rating", "slider", "numeric"].includes(aq.type));
  const npsQ = allQuestions.find((aq) => aq.type === "nps");

  const addNpsPreset = (kind) => {
    if (!npsQ) return;
    const presets = {
      detractors: { question_id: npsQ.id, operator: "between", value: "0,6" },
      passives:   { question_id: npsQ.id, operator: "between", value: "7,8" },
      promoters:  { question_id: npsQ.id, operator: "between", value: "9,10" },
    };
    onChange({ display_when: [...rules, presets[kind]], display_when_match: "any" });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Show this question only if</p>
        {rules.length > 1 && (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid={`display-match-${q.id}`}>
            <button
              type="button"
              onClick={() => onChange({ display_when_match: "all" })}
              className={`px-2.5 py-1 text-[11px] font-semibold ${matchMode === "all" ? "bg-[#F58220] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`display-match-all-${q.id}`}
            >ALL match (AND)</button>
            <button
              type="button"
              onClick={() => onChange({ display_when_match: "any" })}
              className={`px-2.5 py-1 text-[11px] font-semibold border-l border-slate-200 ${matchMode === "any" ? "bg-[#F58220] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`display-match-any-${q.id}`}
            >ANY match (OR)</button>
          </div>
        )}
      </div>
      {allQuestions.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">Add at least one earlier question to use display logic.</p>
      ) : (
        <div className="mt-2 space-y-2">
          {rules.map((r, i) => {
            const refQ = allQuestions.find((aq) => aq.id === r.question_id);
            const isBetween = r.operator === "between";
            const isInList = r.operator === "in" || r.operator === "not_in";
            const hasValue = !["is_empty", "is_not_empty"].includes(r.operator);
            return (
              <div key={i} data-testid={`display-rule-${q.id}-${i}`} className="flex flex-wrap items-center gap-2 bg-amber-50/50 border border-amber-100 rounded-md p-2">
                <select value={r.question_id} onChange={(e) => update(i, { question_id: e.target.value })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs max-w-[180px] truncate">
                  {allQuestions.map((aq, idx) => <option key={aq.id} value={aq.id}>Q{idx + 1}: {aq.title.slice(0, 40)}</option>)}
                </select>
                <select value={r.operator} onChange={(e) => update(i, { operator: e.target.value, value: "" })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs">
                  {OPERATORS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {hasValue && isBetween && (
                  <div className="flex items-center gap-1">
                    <Input
                      value={String(r.value ?? "").split(",")[0] ?? ""}
                      onChange={(e) => {
                        const hi = String(r.value ?? "").split(",")[1] ?? "";
                        update(i, { value: `${e.target.value},${hi}` });
                      }}
                      placeholder="min"
                      className="h-9 w-16 text-xs"
                      data-testid={`display-between-min-${q.id}-${i}`}
                    />
                    <span className="text-xs text-slate-500">and</span>
                    <Input
                      value={String(r.value ?? "").split(",")[1] ?? ""}
                      onChange={(e) => {
                        const lo = String(r.value ?? "").split(",")[0] ?? "";
                        update(i, { value: `${lo},${e.target.value}` });
                      }}
                      placeholder="max"
                      className="h-9 w-16 text-xs"
                      data-testid={`display-between-max-${q.id}-${i}`}
                    />
                  </div>
                )}
                {hasValue && !isBetween && isInList && refQ?.options?.length ? (
                  <MultiOptionPicker
                    options={refQ.options}
                    value={String(r.value ?? "").split(",").map((s) => s.trim()).filter(Boolean)}
                    onChange={(arr) => update(i, { value: arr.join(",") })}
                    testId={`display-in-${q.id}-${i}`}
                  />
                ) : hasValue && !isBetween && isInList ? (
                  <Input value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} placeholder="e.g. 0,1,2,3" className="h-9 w-40 text-xs" data-testid={`display-in-${q.id}-${i}`}/>
                ) : null}
                {hasValue && !isBetween && !isInList && refQ?.type === "mcq" && refQ.options?.length ? (
                  <select value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs max-w-[180px]">
                    <option value="">Select…</option>
                    {refQ.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                  </select>
                ) : hasValue && !isBetween && !isInList ? (
                  <Input value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} placeholder="value" className="h-9 w-32 text-xs"/>
                ) : null}
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 ml-auto" onClick={() => remove(i)}><Trash2 size={13}/></Button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid={`display-add-${q.id}`} size="sm" variant="outline" onClick={() => add()}><Plus size={13} className="mr-1"/>Add condition</Button>
            {npsQ && (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-slate-500 font-medium">NPS presets:</span>
                <Button size="sm" variant="outline" onClick={() => addNpsPreset("detractors")} className="h-8 text-[11px] border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`preset-detractors-${q.id}`}>Detractors (0–6)</Button>
                <Button size="sm" variant="outline" onClick={() => addNpsPreset("passives")} className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50" data-testid={`preset-passives-${q.id}`}>Passives (7–8)</Button>
                <Button size="sm" variant="outline" onClick={() => addNpsPreset("promoters")} className="h-8 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`preset-promoters-${q.id}`}>Promoters (9–10)</Button>
              </div>
            )}
          </div>
          {numericQs.length > 0 && rules.length > 0 && (
            <p className="text-[11px] text-slate-500">Tip: use <span className="font-semibold">between</span> for ranges (e.g. 0 and 6) or <span className="font-semibold">is one of</span> to match several values at once.</p>
          )}
        </div>
      )}
    </div>
  );
}

function MultiOptionPicker({ options, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const toggle = (o) => {
    const next = value.includes(o) ? value.filter((v) => v !== o) : [...value, o];
    onChange(next);
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs min-w-[140px] text-left truncate"
      >
        {value.length === 0 ? "Pick values…" : `${value.length} selected`}
      </button>
      {open && (
        <div className="absolute left-0 mt-1 w-56 max-h-60 overflow-auto bg-white border border-slate-200 rounded-md shadow-elev-2 z-20 p-1">
          {options.map((o, i) => {
            const on = value.includes(o);
            return (
              <button
                type="button"
                key={i}
                onClick={() => toggle(o)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${on ? "bg-[#FFF0DF] text-[#1F3A6B]" : "hover:bg-slate-50"}`}
              >
                <span className={`inline-block w-3.5 h-3.5 border rounded ${on ? "bg-[#F58220] border-[#F58220]" : "border-slate-300"}`}>
                  {on && <Check size={12} className="text-white"/>}
                </span>
                <span className="truncate">{o}</span>
              </button>
            );
          })}
          <div className="flex justify-end pt-1 border-t border-slate-100 mt-1">
            <button type="button" onClick={() => setOpen(false)} className="text-[11px] px-2 py-1 text-slate-600 hover:text-[#1F3A6B]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BranchEditor({ q, allQuestions, onChange }) {
  const rules = q.branch_to || [];
  // Branching can target any question OR end (within the survey, not just before)
  const allInSurvey = allQuestions; // we only know preceding here; full survey branching needs the parent's full list
  const update = (i, patch) => onChange({ branch_to: rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const remove = (i) => onChange({ branch_to: rules.filter((_, idx) => idx !== i) });
  const add = () => onChange({ branch_to: [...rules, { operator: "equals", value: "", jump_to: "end" }] });

  const isNumeric = ["nps", "csat", "ces", "rating", "slider", "numeric"].includes(q.type);
  const hasOptions = ["mcq", "dropdown"].includes(q.type) && Array.isArray(q.options) && q.options.length > 0;

  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">After this question — skip logic (first match wins)</p>
      <div className="mt-2 space-y-2">
        {rules.map((r, i) => {
          const isBetween = r.operator === "between";
          const isInList = r.operator === "in" || r.operator === "not_in";
          return (
            <div key={i} data-testid={`branch-rule-${q.id}-${i}`} className="flex flex-wrap items-center gap-2 bg-[#FFF0DF]/50 border border-[#FFE1BE] rounded-md p-2">
              <span className="text-xs text-slate-500">If answer</span>
              <select value={r.operator} onChange={(e) => update(i, { operator: e.target.value, value: "" })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs">
                {BRANCH_OPERATORS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              {isBetween ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={String(r.value ?? "").split(",")[0] ?? ""}
                    onChange={(e) => {
                      const hi = String(r.value ?? "").split(",")[1] ?? "";
                      update(i, { value: `${e.target.value},${hi}` });
                    }}
                    placeholder="min"
                    className="h-9 w-16 text-xs"
                  />
                  <span className="text-xs text-slate-500">and</span>
                  <Input
                    value={String(r.value ?? "").split(",")[1] ?? ""}
                    onChange={(e) => {
                      const lo = String(r.value ?? "").split(",")[0] ?? "";
                      update(i, { value: `${lo},${e.target.value}` });
                    }}
                    placeholder="max"
                    className="h-9 w-16 text-xs"
                  />
                </div>
              ) : isInList && hasOptions ? (
                <MultiOptionPicker
                  options={q.options}
                  value={String(r.value ?? "").split(",").map((s) => s.trim()).filter(Boolean)}
                  onChange={(arr) => update(i, { value: arr.join(",") })}
                  testId={`branch-in-${q.id}-${i}`}
                />
              ) : isInList ? (
                <Input value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} placeholder="e.g. 0,1,2,3" className="h-9 w-40 text-xs"/>
              ) : hasOptions ? (
                <select value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs max-w-[180px]">
                  <option value="">Select…</option>
                  {q.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <Input value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} placeholder="value" className="h-9 w-24 text-xs"/>
              )}
              <span className="text-xs text-slate-500">jump to</span>
              <select value={r.jump_to} onChange={(e) => update(i, { jump_to: e.target.value })} className="h-9 border border-slate-200 rounded-md px-2 bg-white text-xs max-w-[180px] truncate">
                <option value="end">End of survey</option>
                {allInSurvey.map((aq, idx) => <option key={aq.id} value={aq.id}>Q{idx + 1}: {aq.title.slice(0, 40)}</option>)}
              </select>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 ml-auto" onClick={() => remove(i)}><Trash2 size={13}/></Button>
            </div>
          );
        })}
        <Button data-testid={`branch-add-${q.id}`} size="sm" variant="outline" onClick={add}><Plus size={13} className="mr-1"/>Add skip rule</Button>
        {isNumeric && (
          <p className="text-[11px] text-slate-500">Tip: use <span className="font-semibold">between</span> (e.g. 0 and 6) to branch detractors to a specific question.</p>
        )}
      </div>
    </div>
  );
}

function OptionsEditor({ q, onChange }) {
  const opts = q.options || [];
  const upd = (i, v) => onChange({ options: opts.map((o, idx) => (idx === i ? v : o)) });
  const add = (preset) => onChange({ options: [...opts, preset || `Option ${opts.length + 1}`] });
  const del = (i) => onChange({ options: opts.filter((_, idx) => idx !== i) });
  const replace = (newOpts) => onChange({ options: newOpts });

  const isEmoji = q.type === "emoji";
  const emojiPalette = ["😡", "😞", "😐", "🙂", "😍", "❤️", "⭐", "🔥", "👍", "👎", "🔴", "🟡", "🟢", "✋"];

  // Scale templates — one-click fill
  const templates = [
    { id: "smiley5",  label: "Smileys (5)",        opts: ["😡", "😞", "😐", "🙂", "😍"] },
    { id: "smiley3",  label: "Smileys (3)",        opts: ["😞", "😐", "🙂"] },
    { id: "traffic",  label: "Traffic light (3)",  opts: ["🔴", "🟡", "🟢"] },
    { id: "thumbs",   label: "Thumbs (Yes / No)",  opts: ["👍", "👎"] },
    { id: "hand3",    label: "Bad / OK / Good",    opts: ["👎", "✋", "👍"] },
    { id: "love5",    label: "Love (5)",           opts: ["💔", "😞", "😐", "🙂", "❤️"] },
    { id: "heat5",    label: "Hotness (5)",        opts: ["❄️", "😐", "🙂", "🔥", "💯"] },
  ];

  const renderOptIcon = (o) => {
    if (o === "👍") return <ThumbsUp size={16} className="text-emerald-600 fill-emerald-600 shrink-0"/>;
    if (o === "👎") return <ThumbsDown size={16} className="text-rose-600 fill-rose-600 shrink-0"/>;
    if (o === "🔴") return <span className="w-3.5 h-3.5 rounded-full bg-rose-500 shrink-0"/>;
    if (o === "🟡") return <span className="w-3.5 h-3.5 rounded-full bg-amber-400 shrink-0"/>;
    if (o === "🟢") return <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 shrink-0"/>;
    return null;
  };

  const renderPaletteIcon = (e) => {
    if (e === "👍") return <ThumbsUp size={16} className="text-emerald-600 fill-emerald-600"/>;
    if (e === "👎") return <ThumbsDown size={16} className="text-rose-600 fill-rose-600"/>;
    if (e === "🔴") return <span className="w-4 h-4 rounded-full bg-rose-500"/>;
    if (e === "🟡") return <span className="w-4 h-4 rounded-full bg-amber-400"/>;
    if (e === "🟢") return <span className="w-4 h-4 rounded-full bg-emerald-500"/>;
    return <span className="text-base leading-none">{e}</span>;
  };

  return (
    <div className="space-y-2">
      {isEmoji && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2 pb-2 border-b border-slate-100" data-testid={`q-emoji-templates-${q.id}`}>
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mr-1">Scale templates:</span>
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => replace(t.opts)}
              data-testid={`q-emoji-template-${q.id}-${t.id}`}
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-slate-200 bg-white hover:border-[#F58220] hover:bg-[#FFF0DF] text-slate-700 hover:text-[#1F3A6B] transition flex items-center gap-1"
              title={`Replace options with ${t.label}`}
            >
              <span className="flex items-center gap-0.5">{t.opts.slice(0, 5).map((o, i) => <span key={i}>{renderPaletteIcon(o)}</span>)}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
      {opts.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          {isEmoji && renderOptIcon(o)}
          <Input value={o} onChange={(e) => upd(i, e.target.value)} className="h-9"/>
          <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-600" onClick={() => del(i)}><Trash2 size={14}/></Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => add()}><Plus size={14} className="mr-1"/>Add option</Button>
        {isEmoji && (
          <div className="flex flex-wrap items-center gap-1 ml-1" data-testid={`q-emoji-palette-${q.id}`}>
            <span className="text-[11px] text-slate-500 mr-1">Quick add:</span>
            {emojiPalette.map((e) => {
              const dup = opts.includes(e);
              const testKey = e === "👍" ? "thumbs-up" : e === "👎" ? "thumbs-down" : e === "🔴" ? "red" : e === "🟡" ? "yellow" : e === "🟢" ? "green" : e;
              return (
                <button
                  type="button"
                  key={e}
                  disabled={dup}
                  onClick={() => add(e)}
                  data-testid={`q-emoji-add-${q.id}-${testKey}`}
                  className={`w-8 h-8 rounded-md border grid place-items-center transition ${dup ? "opacity-30 cursor-not-allowed border-slate-200" : "border-slate-200 hover:border-[#F58220] hover:bg-[#FFF0DF]"}`}
                  title={e}
                >
                  {renderPaletteIcon(e)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RangeEditor({ q, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-slate-500">Min</Label>
        <Input type="number" value={q.min ?? 0} onChange={(e) => onChange({ min: Number(e.target.value) })} className="h-9 mt-1"/>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Max</Label>
        <Input type="number" value={q.max ?? 10} onChange={(e) => onChange({ max: Number(e.target.value) })} className="h-9 mt-1"/>
      </div>
    </div>
  );
}

function NumericValidationEditor({ q, onChange }) {
  const v = q.validation || {};
  return (
    <div className="mt-2 grid grid-cols-2 gap-3">
      <div><Label className="text-xs text-slate-500">Min value</Label>
        <Input type="number" value={v.min_value ?? ""} onChange={(e) => onChange({ validation: { ...v, min_value: e.target.value === "" ? null : Number(e.target.value) } })} className="h-9 mt-1"/></div>
      <div><Label className="text-xs text-slate-500">Max value</Label>
        <Input type="number" value={v.max_value ?? ""} onChange={(e) => onChange({ validation: { ...v, max_value: e.target.value === "" ? null : Number(e.target.value) } })} className="h-9 mt-1"/></div>
    </div>
  );
}

function DateValidationEditor({ q, onChange }) {
  const v = q.validation || {};
  return (
    <div className="mt-2 grid grid-cols-2 gap-3">
      <div><Label className="text-xs text-slate-500">Earliest allowed</Label>
        <Input type="date" value={v.min_date || ""} onChange={(e) => onChange({ validation: { ...v, min_date: e.target.value || null } })} className="h-9 mt-1"/></div>
      <div><Label className="text-xs text-slate-500">Latest allowed</Label>
        <Input type="date" value={v.max_date || ""} onChange={(e) => onChange({ validation: { ...v, max_date: e.target.value || null } })} className="h-9 mt-1"/></div>
    </div>
  );
}

function FileExtensionsEditor({ q, onChange }) {
  const exts = (q.allowed_extensions || []).join(", ");
  return (
    <div className="mt-2">
      <Label className="text-xs text-slate-500">Allowed file extensions (comma-separated)</Label>
      <Input
        data-testid={`q-extensions-${q.id}`}
        value={exts}
        onChange={(e) => onChange({ allowed_extensions: e.target.value.split(",").map((s) => s.trim().replace(/^\./, "").toLowerCase()).filter(Boolean) })}
        placeholder="pdf, jpg, jpeg, png"
        className="h-9 mt-1"
      />
    </div>
  );
}

function ExtraSettings({ q, onChange }) {
  const [open, setOpen] = useState(false);
  const supportsDefault = ["text","long_text","numeric","slider","rating","csat","ces","nps","date","time","email","phone","mcq","dropdown","emoji"].includes(q.type);
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`q-extra-${q.id}`} className="text-xs text-[#1F3A6B] font-semibold hover:underline">
        {open ? "▾" : "▸"} Default value & weight
      </button>
      {open && (
        <div className="mt-2 grid sm:grid-cols-2 gap-3 bg-slate-50 border border-slate-100 rounded-md p-3">
          {supportsDefault && (
            <div>
              <Label className="text-xs text-slate-500">Default value (pre-fills the answer)</Label>
              <Input
                data-testid={`q-default-${q.id}`}
                value={q.default_value ?? ""}
                onChange={(e) => onChange({ default_value: e.target.value === "" ? null : e.target.value })}
                placeholder={q.type === "date" ? "YYYY-MM-DD" : q.type === "time" ? "HH:MM" : "—"}
                type={["numeric","slider","rating","csat","ces","nps"].includes(q.type) ? "number" : (q.type === "date" ? "date" : (q.type === "time" ? "time" : "text"))}
                className="h-9 mt-1"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Weight (% of weighted score)</Label>
            <Input
              data-testid={`q-weight-${q.id}`}
              type="number" min={0} max={100} step={5}
              value={q.weight === null || q.weight === undefined ? "" : Math.round((q.weight) * 100)}
              onChange={(e) => onChange({ weight: e.target.value === "" ? null : Math.max(0, Math.min(100, Number(e.target.value))) / 100 })}
              placeholder="e.g. 40"
              className="h-9 mt-1"
            />
            <p className="mt-1 text-[10px] text-slate-500">Used for weighted scoring (e.g. Service 40% + Product 30% + Cleanliness 30%).</p>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionPreview({ q }) {
  if (!q) return null;
  const isHtmlTitle = /<\/?[a-z][\s\S]*>/i.test(String(q.title || ""));
  const isHtmlDesc = /<\/?[a-z][\s\S]*>/i.test(String(q.description || ""));
  return (
    <div>
      <p className="text-base font-semibold text-[#1F3A6B]">
        {isHtmlTitle
          ? <span className="rt-render" dangerouslySetInnerHTML={{ __html: (q.title || "").replace(/^<p>([\s\S]*)<\/p>$/i, "$1") }}/>
          : q.title}
        {q.required && <span className="text-rose-500 ml-1">*</span>}
      </p>
      {q.description && (
        isHtmlDesc
          ? <div className="text-sm text-slate-500 mt-1 rt-render" dangerouslySetInnerHTML={{ __html: q.description }}/>
          : <p className="text-sm text-slate-500 mt-1">{q.description}</p>
      )}
      <div className="mt-4">
        {q.type === "nps" && <ScalePreview min={q.min ?? 0} max={q.max ?? 10}/>}
        {q.type === "csat" && <ScalePreview min={q.min ?? 1} max={q.max ?? 5}/>}
        {q.type === "ces" && <ScalePreview min={q.min ?? 1} max={q.max ?? 7}/>}
        {q.type === "rating" && <StarsPreview max={q.max ?? 5}/>}
        {q.type === "slider" && <SliderPreview min={q.min ?? 0} max={q.max ?? 100}/>}
        {q.type === "emoji" && <EmojiPreview opts={q.options || ["😡","😞","😐","🙂","😍"]}/>}
        {(q.type === "mcq" || q.type === "dropdown") && (
          <div className="space-y-2">{(q.options || []).map((o, i) => <div key={i} className="border border-slate-200 rounded-md px-3 py-2 text-sm">{o}</div>)}</div>
        )}
        {q.type === "multi_select" && (
          <div className="space-y-2">{(q.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm"><input type="checkbox" disabled/> {o}</label>)}</div>
        )}
        {q.type === "ranking" && (
          <div className="space-y-2">{(q.options || []).map((o, i) => <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm"><span className="font-bold text-[#F58220] mr-1">{i + 1}.</span>{o}</div>)}</div>
        )}
        {q.type === "text" && <Input placeholder={q.placeholder || "Your answer"} disabled className="h-10"/>}
        {q.type === "long_text" && <Textarea placeholder={q.placeholder || "Your detailed feedback"} disabled rows={3}/>}
        {q.type === "email" && <Input type="email" placeholder={q.placeholder || "you@example.com"} disabled className="h-10"/>}
        {q.type === "phone" && <Input type="tel" placeholder={q.placeholder || "+1 555 0100"} disabled className="h-10"/>}
        {q.type === "numeric" && <Input type="number" placeholder={q.placeholder || "Number"} disabled className="h-10"/>}
        {q.type === "date" && <Input type="date" disabled className="h-10"/>}
        {q.type === "time" && <Input type="time" disabled className="h-10"/>}
        {q.type === "image_choice" && (
          <div className="grid grid-cols-2 gap-2">
            {(q.options || []).map((url, i) => (
              <div key={i} className="border border-slate-200 rounded-md overflow-hidden">
                <img src={url} alt="" className="w-full h-24 object-cover" onError={(e) => (e.currentTarget.style.display = "none")}/>
              </div>
            ))}
          </div>
        )}
        {q.type === "file_upload" && <div className="border-2 border-dashed border-slate-200 rounded-md p-4 text-center text-sm text-slate-500">Click to upload a file</div>}
      </div>
    </div>
  );
}

function ScalePreview({ min, max }) {
  const items = [];
  for (let i = min; i <= max; i++) items.push(i);
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((n) => (
        <div key={n} className="w-9 h-9 rounded-md border border-slate-200 grid place-items-center text-sm font-semibold text-slate-700 hover:border-[#F58220] hover:bg-[#FFF0DF]">{n}</div>
      ))}
    </div>
  );
}
function StarsPreview({ max }) {
  return <div className="flex gap-1">{[...Array(max)].map((_, i) => <Star key={i} size={22} className="text-amber-400"/>)}</div>;
}
function SliderPreview({ min, max }) {
  return <div><input type="range" disabled min={min} max={max} defaultValue={(min + max) / 2} className="w-full"/><div className="flex justify-between text-xs text-slate-500 mt-1"><span>{min}</span><span>{max}</span></div></div>;
}
function EmojiPreview({ opts }) {
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map((e, i) => {
        const isUp = e === "👍";
        const isDown = e === "👎";
        const isRed = e === "🔴";
        const isYellow = e === "🟡";
        const isGreen = e === "🟢";
        return (
          <div key={i} className="w-10 h-10 grid place-items-center text-xl border border-slate-200 rounded-md">
            {isUp ? <ThumbsUp size={20} className="text-emerald-600 fill-emerald-600"/>
              : isDown ? <ThumbsDown size={20} className="text-rose-600 fill-rose-600"/>
              : isRed ? <span className="w-5 h-5 rounded-full bg-rose-500"/>
              : isYellow ? <span className="w-5 h-5 rounded-full bg-amber-400"/>
              : isGreen ? <span className="w-5 h-5 rounded-full bg-emerald-500"/>
              : e}
          </div>
        );
      })}
    </div>
  );
}

function TranslateButton({ surveyId, survey, setSurvey }) {
  const [open, setOpen] = React.useState(false);
  const [lang, setLang] = React.useState("Spanish");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const langs = [
    "Spanish", "Hindi", "French", "German", "Portuguese", "Italian",
    "Arabic", "Japanese", "Chinese (Simplified)", "Korean", "Russian", "Turkish",
    "Dutch", "Polish", "Vietnamese", "Indonesian", "Thai", "Hebrew",
  ];
  const existing = Object.keys(survey?.translations || {});
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/ai/translate", { survey_id: surveyId, target_language: lang });
      setSurvey(data);
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Translation failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="relative">
      <Button data-testid="builder-translate-btn" type="button" variant="outline" onClick={() => setOpen((o) => !o)}>
        <Languages size={16} className="mr-1.5"/>Translate
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-lg shadow-elev-2 p-4 z-20">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">AI translate (GPT-5.2)</p>
          {existing.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {existing.map((l) => <span key={l} className="text-[10px] uppercase font-bold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-2 py-0.5">{l}</span>)}
            </div>
          )}
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="mt-3 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
            {langs.map((l) => <option key={l}>{l}</option>)}
          </select>
          {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
          <div className="mt-3 flex gap-2">
            <Button data-testid="builder-translate-cancel" size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="builder-translate-submit" size="sm" disabled={busy} onClick={submit} className="bg-[#F58220] hover:bg-[#E36F0E] text-white ml-auto">{busy ? "Translating…" : "Translate"}</Button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Adds a language version respondents can switch to on the public link.</p>
        </div>
      )}
    </div>
  );
}

/* ---------- Theme colors ---------- */
const THEME_DEFAULTS = {
  primary: "#F58220",
  accent: "#F58220",
  text: "#1F3A6B",
  button_text: "#FFFFFF",
  background: "#FFFFFF",
  option_selected_bg: "",
  option_selected_text: "",
  background_image_url: "",
  background_overlay: 0.35,
};

const THEME_SWATCHES = [
  "#0B3C5D", "#F58220", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#0F172A", "#475569", "#FFFFFF",
];

function ColorField({ label, value, onChange, testId, helper }) {
  const [open, setOpen] = React.useState(false);
  const wrap = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={wrap} className="relative">
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testId}-swatch`}
          className="h-9 w-9 rounded-md border-2 border-slate-200 shadow-sm shrink-0"
          style={{ backgroundColor: value }}
          aria-label={`${label} color`}
        />
        <Input
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 font-mono text-xs"
          placeholder="#F58220"
        />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-9 w-9 rounded border border-slate-200 cursor-pointer p-0"
          aria-label={`${label} picker`}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-elev-2 p-2 w-52 grid grid-cols-5 gap-1.5">
          {THEME_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { onChange(c); setOpen(false); }}
              className="w-8 h-8 rounded border border-slate-200 hover:scale-110 transition"
              style={{ backgroundColor: c }}
              title={c}
              data-testid={`${testId}-preset-${c.replace("#", "")}`}
            />
          ))}
        </div>
      )}
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

function ThemeColorsEditor({ theme, onChange }) {
  const [open, setOpen] = React.useState(false);
  const val = (k) => theme[k] ?? THEME_DEFAULTS[k];
  const valOr = (k, fallback) => (theme[k] && theme[k] !== "" ? theme[k] : fallback);
  const reset = () => onChange({ ...THEME_DEFAULTS });
  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="builder-theme-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-[#1F3A6B] hover:text-[#082F48]"
      >
        <span className="inline-flex gap-1">
          <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ backgroundColor: val("primary") }}/>
          <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ backgroundColor: val("accent") }}/>
          <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ backgroundColor: val("text") }}/>
        </span>
        Theme colors
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorField label="Button background" value={val("primary")} onChange={(v) => onChange({ primary: v })} testId="theme-primary" helper="Main call-to-action buttons."/>
            <ColorField label="Button text" value={val("button_text")} onChange={(v) => onChange({ button_text: v })} testId="theme-button-text" helper="Text on primary buttons."/>
            <ColorField label="Body / question text" value={val("text")} onChange={(v) => onChange({ text: v })} testId="theme-text" helper="Question titles & body copy."/>
            <ColorField label="Accent" value={val("accent")} onChange={(v) => onChange({ accent: v })} testId="theme-accent" helper="Progress bar & subtle highlights."/>
            <ColorField label="Page background" value={val("background")} onChange={(v) => onChange({ background: v })} testId="theme-background" helper="Public survey background."/>
            <ColorField label="Selected option background" value={valOr("option_selected_bg", val("primary"))} onChange={(v) => onChange({ option_selected_bg: v })} testId="theme-option-bg" helper="MCQ / dropdown / multi-select selected fill (defaults to button bg)."/>
            <ColorField label="Selected option text" value={valOr("option_selected_text", val("button_text"))} onChange={(v) => onChange({ option_selected_text: v })} testId="theme-option-text" helper="Text on selected MCQ / multi-select chips."/>
          </div>

          {/* Background image (optional) */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Background image (optional)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Image URL</Label>
                <Input
                  data-testid="theme-bg-image-url"
                  value={theme.background_image_url || ""}
                  onChange={(e) => onChange({ background_image_url: e.target.value })}
                  placeholder="https://images.unsplash.com/photo-...  (leave blank to use page color)"
                  className="mt-1 h-10 rounded-sm"
                />
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="theme-bg-image-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 1_500_000) { alert("Please pick an image under 1.5MB."); return; }
                      const reader = new FileReader();
                      reader.onload = () => onChange({ background_image_url: String(reader.result || "") });
                      reader.readAsDataURL(f);
                    }}
                    className="text-xs"
                  />
                  {theme.background_image_url && (
                    <Button type="button" size="sm" variant="outline" data-testid="theme-bg-image-clear" onClick={() => onChange({ background_image_url: "" })}>
                      Remove
                    </Button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Full-cover fixed background behind the survey card. Under 1.5MB recommended.</p>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  Dark overlay ({Math.round((theme.background_overlay ?? 0.35) * 100)}%)
                </Label>
                <input
                  type="range" min={0} max={0.85} step={0.05}
                  value={theme.background_overlay ?? 0.35}
                  onChange={(e) => onChange({ background_overlay: Number(e.target.value) })}
                  data-testid="theme-bg-overlay"
                  className="mt-3 w-full accent-[#F58220]"
                  disabled={!theme.background_image_url}
                />
                <p className="mt-1 text-[11px] text-slate-500">Darkens the image so question text stays readable. Ignored when no background image is set.</p>
                {theme.background_image_url && (
                  <div className="mt-2 h-16 rounded-md border border-slate-200 overflow-hidden relative"
                    style={{ backgroundImage: `url("${theme.background_image_url}")`, backgroundSize: "cover", backgroundPosition: "center" }}>
                    <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${theme.background_overlay ?? 0.35})` }}/>
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white">Preview</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={reset} data-testid="theme-reset">Reset to brand defaults</Button>
            <div className="ml-auto flex flex-wrap items-center gap-3 rounded-lg px-3 py-2 border border-slate-200" style={{ backgroundColor: val("background") }}>
              <span className="text-xs" style={{ color: val("text") }}>Preview:</span>
              <span className="px-3 py-1.5 rounded-md text-xs font-semibold" style={{ backgroundColor: val("primary"), color: val("button_text") }}>Submit</span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border-2" style={{ backgroundColor: valOr("option_selected_bg", val("primary")), borderColor: valOr("option_selected_bg", val("primary")), color: valOr("option_selected_text", val("button_text")) }}>
                ✓ Option
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ---------- Schedule / Status / Restrictions ---------- */
function SurveyScheduleEditor({ survey, setField }) {
  const [open, setOpen] = React.useState(false);
  const status = survey.status || "draft";
  const set = (k, v) => setField(k, v);

  const toLocalInputValue = (iso) => {
    if (!iso) return "";
    try { const d = new Date(iso); const pad = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; } catch (_) { return ""; }
  };
  const fromLocalInputValue = (v) => v ? new Date(v).toISOString() : null;

  const badge = { draft: "bg-slate-100 text-slate-700", scheduled: "bg-amber-100 text-amber-700", published: "bg-emerald-100 text-emerald-700", paused: "bg-orange-100 text-orange-700", archived: "bg-rose-100 text-rose-700" }[status];

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="builder-schedule-toggle"
        className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink-900)" }}>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${badge}`}>{status}</span>
        Schedule & restrictions
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Status</Label>
            <select data-testid="builder-status" value={status} onChange={(e) => set("status", e.target.value)}
              className="mt-1 h-10 w-full rounded-sm border px-2 text-sm" style={{ borderColor: "var(--border)" }}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published / Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived / Closed</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Opens at (Scheduled)</Label>
            <Input data-testid="builder-starts-at" type="datetime-local" value={toLocalInputValue(survey.starts_at)}
              onChange={(e) => set("starts_at", fromLocalInputValue(e.target.value))} className="mt-1 h-10 rounded-sm"/>
            <p className="text-[11px] mt-1" style={{ color: "var(--ink-400)" }}>Used when status = Scheduled.</p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Expires at</Label>
            <Input data-testid="builder-expires-at" type="datetime-local" value={toLocalInputValue(survey.expires_at)}
              onChange={(e) => set("expires_at", fromLocalInputValue(e.target.value))} className="mt-1 h-10 rounded-sm"/>
            <p className="text-[11px] mt-1" style={{ color: "var(--ink-400)" }}>After this, the public URL shows &quot;This survey has ended&quot;.</p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Cooldown (days)</Label>
            <Input data-testid="builder-cooldown" type="number" min={0} value={survey.cooldown_days ?? 0}
              onChange={(e) => set("cooldown_days", Math.max(0, Number(e.target.value) || 0))} className="mt-1 h-10 rounded-sm"/>
            <p className="text-[11px] mt-1" style={{ color: "var(--ink-400)" }}>Same email/phone cannot resubmit within N days. 0 disables.</p>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <Switch data-testid="builder-once-per-ref" checked={survey.once_per_ref === true}
              onCheckedChange={(v) => set("once_per_ref", v)}/>
            <span className="text-sm" style={{ color: "var(--ink-700)" }}>One response per transaction (ref_id) — recommended for POS integrations</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Per-NPS-category post-response Journey editor ---------- */
function SurveyJourneysEditor({ journeys, onChange }) {
  const [open, setOpen] = React.useState(false);
  const set = (cat, patch) => onChange({ ...(journeys || {}), [cat]: { ...(journeys?.[cat] || {}), ...patch } });
  const blocks = [
    { key: "detractor", label: "Detractors (0–6)", tone: "rose",    swatch: "bg-rose-500" },
    { key: "passive",   label: "Passives (7–8)",   tone: "amber",   swatch: "bg-amber-400" },
    { key: "promoter",  label: "Promoters (9–10)", tone: "emerald", swatch: "bg-emerald-500" },
  ];

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="builder-journeys-toggle"
        className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink-900)" }}>
        <MessageSquare size={14}/>
        Post-response journey (per NPS category)
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs" style={{ color: "var(--ink-500)" }}>Customize what respondents see after they submit — different journey for detractors, passives, and promoters. E.g., send promoters to a Google review, hand detractors a recovery coupon.</p>
          {blocks.map((b) => {
            const j = journeys?.[b.key] || {};
            return (
              <div key={b.key} className="rounded-sm p-3" style={{ border: "1px solid var(--border)", backgroundColor: "#FFFFFF" }} data-testid={`journey-${b.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-3 h-3 rounded-full ${b.swatch}`}/>
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--ink-700)" }}>{b.label}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Thank-you message (overrides survey-level)</Label>
                    <div className="mt-1">
                      <RichEditor
                        testId={`journey-thanks-${b.key}`}
                        value={j.thank_you_message || ""}
                        onChange={(v) => set(b.key, { thank_you_message: v })}
                        placeholder="Custom thanks — bold, colors, links…"
                        minHeight={40}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Redirect URL (button)</Label>
                    <Input data-testid={`journey-url-${b.key}`} value={j.redirect_url || ""}
                      onChange={(e) => set(b.key, { redirect_url: e.target.value })}
                      placeholder="https://g.page/r/…" className="mt-1 h-10 rounded-sm"/>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Redirect button label</Label>
                    <Input data-testid={`journey-label-${b.key}`} value={j.redirect_label || ""}
                      onChange={(e) => set(b.key, { redirect_label: e.target.value })}
                      placeholder="e.g. Leave us a Google review" className="mt-1 h-10 rounded-sm"/>
                  </div>
                  <div className="sm:col-span-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                    <div className="flex items-center gap-2 mt-2">
                      <Switch data-testid={`journey-coupon-toggle-${b.key}`} checked={!!j.coupon_enabled}
                        onCheckedChange={(v) => set(b.key, { coupon_enabled: v })}/>
                      <span className="text-sm font-semibold" style={{ color: "var(--ink-700)" }}>Issue a unique coupon code</span>
                      <span className="text-[11px] ml-auto" style={{ color: "var(--ink-400)" }}>Each response gets a code like SUB-A9F3K2</span>
                    </div>
                    {j.coupon_enabled && (
                      <div className="mt-2 grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Coupon prefix</Label>
                          <Input data-testid={`journey-coupon-prefix-${b.key}`} value={j.coupon_prefix || ""}
                            onChange={(e) => set(b.key, { coupon_prefix: e.target.value.toUpperCase().slice(0, 8) })}
                            placeholder="SUB" className="mt-1 h-10 rounded-sm font-mono"/>
                        </div>
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--ink-500)" }}>Coupon message</Label>
                          <Input data-testid={`journey-coupon-msg-${b.key}`} value={j.coupon_message || ""}
                            onChange={(e) => set(b.key, { coupon_message: e.target.value })}
                            placeholder="Here's 10% off your next visit" className="mt-1 h-10 rounded-sm"/>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

